'use client'

import { PlayCircleIcon } from '@heroicons/react/24/solid'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'

import { Carousel } from '@src/components/Carousel/Carousel'
import { BrightcovePlayer } from '@src/components/VideoPlayer/BrightcovePlayer'

interface MediaItem {
  mediaType: 'photo' | 'video'
  desktopImgSrc?: string
  mobileImgSrc?: string
  brightcoveVideoId?: string
  videoThumbnailImgSrc?: string
  videoPlayerAspectRatio?: '16 / 9' | '9 / 16'
  alt?: string
}

interface MediaGallery {
  mediaItems: MediaItem[]
}

function MediaItem({ mediaItem }: { mediaItem?: MediaItem }) {
  if (mediaItem?.mediaType === 'photo' && mediaItem?.desktopImgSrc) {
    return (
      <Image
        className="rounded-[30px]"
        src={mediaItem.desktopImgSrc}
        alt={mediaItem.alt ?? ''}
        width={662}
        height={662}
      ></Image>
    )
  }
  if (mediaItem?.mediaType === 'video' && mediaItem?.brightcoveVideoId) {
    return (
      <BrightcovePlayer
        videoId={mediaItem?.brightcoveVideoId}
        videoPlayerAspectRatio={mediaItem?.videoPlayerAspectRatio}
        isMuted={false}
        autoplay={false}
      />
    )
  }

  return null
}

export function MediaGallery({ mediaItems = [] }: MediaGallery) {
  const [selectedMediaItem, setSelectedMediaItem] = useState<MediaItem>()
  useEffect(() => {
    setSelectedMediaItem(mediaItems[0])
  }, [mediaItems])

  return (
    <div className="mb-4 lg:mb-0">
      <div className="lg:hidden">
        <Carousel
          slides={mediaItems.map((mediaItem, index) => (
            <div className="flex size-full items-center justify-center" key={`${index}`}>
              {mediaItem?.mediaType === 'photo' && mediaItem?.mobileImgSrc && (
                <Image
                  className="w-full max-w-[85vw] rounded-[30px] object-contain"
                  src={mediaItem?.mobileImgSrc}
                  alt={mediaItem.alt ?? ''}
                  width={332}
                  height={332}
                />
              )}
              {mediaItem?.mediaType === 'video' && mediaItem?.brightcoveVideoId && (
                <BrightcovePlayer
                  videoId={mediaItem?.brightcoveVideoId}
                  videoPlayerAspectRatio={mediaItem?.videoPlayerAspectRatio}
                  isMuted={false}
                  autoplay={false}
                />
              )}
            </div>
          ))}
          overflowMobile
          showArrows={false}
          alignmentMobile="center"
          dotsStyle={{ position: 'Outside Slider', selectedColor: '#000', deselectedColor: '#ccc' }}
          builderBlock={{}}
          slidesPerView={1}
          attributes={[]}
        />
      </div>
      <div className="mb-6 hidden lg:flex lg:gap-8">
        <div className="w-[102px]">
          <div className="flex cursor-pointer flex-col gap-2">
            {mediaItems.map((mediaItem, index) => (
              <div
                className="w-full overflow-hidden rounded-lg"
                role="presentation"
                key={`${index}`}
                onClick={() => setSelectedMediaItem(mediaItem)}
              >
                {mediaItem?.desktopImgSrc && (
                  <Image
                    src={mediaItem.desktopImgSrc}
                    alt={mediaItem.alt ?? ''}
                    height={100}
                    width={100}
                  />
                )}
                {mediaItem?.mediaType === 'video' && mediaItem?.brightcoveVideoId && (
                  <div className="relative flex size-[100px] items-center justify-center">
                    {mediaItem?.videoThumbnailImgSrc && (
                      <Image
                        src={mediaItem?.videoThumbnailImgSrc}
                        alt={mediaItem.alt ?? ''}
                        height={100}
                        width={100}
                      />
                    )}
                    <div className="absolute top-0 size-[100px] rounded-lg bg-bodi-gray-medium opacity-50"></div>
                    <div className="absolute top-0 flex size-[100px] items-center justify-center rounded-lg">
                      <PlayCircleIcon fill="white" fillOpacity={0.8} className="size-9" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* negative margin needed so all images are all aligned */}
        <div className="flex w-full max-w-full flex-col ">
          <MediaItem mediaItem={selectedMediaItem} />
        </div>
      </div>
    </div>
  )
}
